import { createTheme, alpha } from '@mui/material/styles'

// =============================================================================
// DASH2 DESIGN SYSTEM - Bold, Industrial, Data-Forward
// =============================================================================

const tokens = {
  // Border radius scale - sharper for industrial feel
  radius: {
    xs: 2,
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999
  },
  // Shadow scale - dramatic depth
  shadows: {
    none: 'none',
    xs: '0 1px 3px rgba(0,0,0,0.12)',
    sm: '0 2px 8px rgba(0,0,0,0.15)',
    md: '0 4px 16px rgba(0,0,0,0.2)',
    lg: '0 8px 32px rgba(0,0,0,0.25)',
    xl: '0 16px 64px rgba(0,0,0,0.3)',
    // Colored glows for brand elements
    glow: {
      primary: '0 0 40px rgba(160, 206, 78, 0.4)',
      accent: '0 0 40px rgba(255, 107, 53, 0.4)',
      success: '0 0 40px rgba(78, 205, 196, 0.4)',
      info: '0 0 40px rgba(99, 179, 237, 0.4)'
    },
    // Inner shadows for depth
    inner: 'inset 0 2px 4px rgba(0,0,0,0.2)',
    // Card hover effect
    card: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)'
  },
  // Transition presets
  transitions: {
    fast: '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
    spring: '0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  },
  // Blur values for glassmorphism
  blur: {
    sm: 'blur(8px)',
    md: 'blur(16px)',
    lg: 'blur(24px)'
  }
}

// =============================================================================
// DASH2 COLOR PALETTES - Dark & Light Themes
// =============================================================================

// Shared colors that work on both themes
const sharedColors = {
  primary: {
    main: '#A0CE4E',      // Powertron green
    light: '#C5E87D',
    dark: '#7CB342',
    darker: '#558B2F',
    contrastText: '#0A0A0A'
  },
  accent: {
    orange: '#FF6B35',
    teal: '#4ECDC4',
    blue: '#63B3ED',
    purple: '#9F7AEA',
    pink: '#F687B3'
  },
  efficiency: {
    excellent: '#4ECDC4',
    good: '#A0CE4E',
    average: '#FFD93D',
    below: '#FF6B35',
    poor: '#E53E3E'
  },
  success: {
    main: '#4ECDC4',
    light: '#76E4DB',
    dark: '#38B2AC',
    contrastText: '#0A0A0A'
  },
  info: {
    main: '#63B3ED',
    light: '#90CDF4',
    dark: '#4299E1',
    contrastText: '#0A0A0A'
  },
  warning: {
    main: '#FFD93D',
    light: '#FFEAA7',
    dark: '#F6AD55',
    contrastText: '#0A0A0A'
  },
  error: {
    main: '#E53E3E',
    light: '#FC8181',
    dark: '#C53030',
    contrastText: '#ffffff'
  },
  chart: {
    primary: ['#A0CE4E', '#7CB342', '#558B2F'],
    categorical: [
      '#A0CE4E', '#FF6B35', '#4ECDC4', '#63B3ED',
      '#9F7AEA', '#F687B3', '#FFD93D', '#FC8181'
    ],
    gradient: {
      primary: ['rgba(160, 206, 78, 0.9)', 'rgba(160, 206, 78, 0.1)'],
      accent: ['rgba(255, 107, 53, 0.9)', 'rgba(255, 107, 53, 0.1)'],
      teal: ['rgba(78, 205, 196, 0.9)', 'rgba(78, 205, 196, 0.1)']
    }
  },
  // Semantic color tokens — map meaning to color
  semantic: {
    savings: '#A0CE4E',       // Financial gains, money saved
    roi: '#C5E87D',           // ROI, payback metrics
    energy: '#4ECDC4',        // Energy reduction, kW/ton
    environmental: '#4CAF50', // CO2, sustainability
    info: '#63B3ED',          // Coverage, counts, neutral data
    analysis: '#9F7AEA',      // ML, research metrics
    attention: '#FF6B35',     // Comparisons, highlights
    cost: '#E53E3E',          // Losses, degradation
    benchmark: '#FFD93D'      // Baseline references
  }
}

// Dark theme palette
const darkPalette = {
  mode: 'dark',
  ...sharedColors,
  primary: {
    main: '#C5E87D',          // Brighter green for dark mode contrast
    light: '#D4F19D',
    dark: '#A0CE4E',
    darker: '#7CB342',
    contrastText: '#0A0A0A'
  },
  secondary: {
    main: '#1A1A2E',
    light: '#2D2D44',
    dark: '#0F0F1A',
    contrastText: '#ffffff'
  },
  background: {
    default: '#0F1419',       // Deep space with subtle green tint
    paper: '#1A1F25',         // Card background
    elevated: '#242931',      // Hover/elevated state
    surface: '#1E1E1E',
    subtle: '#242424'
  },
  text: {
    primary: '#F4F6F8',       // Crisp white
    secondary: '#8C9BA5',     // Muted grey
    disabled: '#454F5B'
  },
  divider: 'rgba(255, 255, 255, 0.08)'
}

// Light theme palette - Uses DARKER accent colors for contrast on white
const lightPalette = {
  mode: 'light',
  ...sharedColors,
  // Override accent colors with darker variants for light mode
  accent: {
    orange: '#C74600',    // Burnt orange (was #FF6B35) - 4.6:1 contrast
    teal: '#0D7377',      // Deep teal (was #4ECDC4) - 5.8:1 contrast
    blue: '#1A6BB8',      // Royal blue (was #63B3ED) - 4.9:1 contrast
    purple: '#6B46C1',    // Deep purple (was #9F7AEA)
    pink: '#C53887'       // Deep pink (was #F687B3)
  },
  // Override success/info with darker colors
  success: {
    main: '#0D7377',      // Deep teal for light mode
    light: '#4ECDC4',
    dark: '#095658',
    contrastText: '#ffffff'
  },
  info: {
    main: '#1A6BB8',      // Royal blue for light mode
    light: '#63B3ED',
    dark: '#145591',
    contrastText: '#ffffff'
  },
  // ESG-specific green for text (darker for readability)
  esg: {
    main: '#5B7F24',      // Atlassian success green (5.2:1 contrast)
    light: '#6B9B3D',
    dark: '#3D6A1C'
  },
  primary: {
    main: '#5B7F24',      // Atlassian success green - professional olive (was #7CB342)
    light: '#6B9B3D',
    dark: '#3D6A1C',
    darker: '#2D5015',
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#F0F2F5',
    light: '#FFFFFF',
    dark: '#E0E4E8',
    contrastText: '#1A1A1A'
  },
  background: {
    default: '#F8F9FA',
    paper: '#FFFFFF',
    elevated: '#FFFFFF',
    surface: '#F0F2F5',
    subtle: '#E8EAED'
  },
  text: {
    primary: '#1A1A1A',
    secondary: '#666666',
    disabled: '#999999'
  },
  divider: 'rgba(0, 0, 0, 0.08)'
}

// Default export uses dark palette
const palette = darkPalette

// =============================================================================
// DARK MODE SHADOW HELPERS - Green-tinted glows
// =============================================================================

const darkModeShadows = {
  card: '0 2px 8px rgba(160, 206, 78, 0.08)',
  cardHover: '0 12px 32px rgba(197, 232, 125, 0.2)',
  elevated: '0 8px 24px rgba(160, 206, 78, 0.12)',
  glow: '0 0 20px rgba(197, 232, 125, 0.3)',
  glowSubtle: '0 0 10px rgba(160, 206, 78, 0.15)'
}

// =============================================================================
// DASH2 TYPOGRAPHY - Bold & Distinctive
// =============================================================================

const typography = {
  // Outfit for display, Plus Jakarta Sans for body, JetBrains Mono for data
  fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
  fontFamilyDisplay: '"Outfit", "Plus Jakarta Sans", sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", monospace',

  h1: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '4rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.04em'
  },
  h2: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.03em'
  },
  h3: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em'
  },
  h4: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-0.015em'
  },
  h5: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.3
  },
  h6: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.35
  },
  subtitle1: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em'
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.05em',
    textTransform: 'uppercase'
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.7,
    letterSpacing: '0.01em'
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.65,
    letterSpacing: '0.01em'
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    letterSpacing: '0.03em',
    color: palette.text.secondary
  },
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.15em',
    textTransform: 'uppercase'
  },
  button: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    textTransform: 'none',
    fontWeight: 600,
    letterSpacing: '0.02em'
  },
  // Custom data display variant
  dataLarge: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '-0.02em'
  },
  dataMedium: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.1,
    letterSpacing: '-0.01em'
  },
  dataSmall: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.2
  },
  // NEW: Premium display typography for EAS Calculator
  display: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '4.5rem',     // 72px - HUGE metric values
    fontWeight: 800,
    lineHeight: 0.9,
    letterSpacing: '-0.03em'
  },
  hero: {
    fontFamily: '"Outfit", sans-serif',
    fontSize: '2.5rem',     // 40px - Section headers
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em'
  },
  stat: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '2rem',       // 32px - Supporting metrics
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.01em'
  },
  data: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '1.25rem',    // 20px - Financial figures
    fontWeight: 600,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums'
  },
  label: {
    fontSize: '0.75rem',    // 12px - De-emphasized context
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.05em',
    textTransform: 'uppercase'
  }
}

// =============================================================================
// THEME CREATION FUNCTION
// =============================================================================

function createAppTheme(mode = 'dark') {
  const selectedPalette = mode === 'dark' ? darkPalette : lightPalette

  return createTheme({
  palette: selectedPalette,
  typography,
  shape: {
    borderRadius: tokens.radius.md
  },
  shadows: [
    tokens.shadows.none,
    tokens.shadows.xs,
    tokens.shadows.xs,
    tokens.shadows.sm,
    tokens.shadows.sm,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl
  ],

  // ===========================================================================
  // DASH2 COMPONENT OVERRIDES
  // ===========================================================================
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box'
        },
        html: {
          scrollBehavior: 'smooth',
          backgroundColor: selectedPalette.background.default
        },
        body: {
          backgroundColor: selectedPalette.background.default,
          color: selectedPalette.text.primary,
          scrollbarWidth: 'thin',
          scrollbarColor: `${selectedPalette.text.disabled} transparent`,
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: selectedPalette.text.disabled,
            borderRadius: tokens.radius.full,
            '&:hover': {
              background: selectedPalette.text.secondary
            }
          }
        },
        '::selection': {
          backgroundColor: alpha(selectedPalette.primary.main, 0.3),
          color: selectedPalette.text.primary
        }
      }
    },

    // -------------------------------------------------------------------------
    // BUTTONS - Bold, gradient-accented
    // -------------------------------------------------------------------------
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: tokens.radius.sm,
          padding: '12px 24px',
          transition: `all ${tokens.transitions.fast}`,
          position: 'relative',
          overflow: 'hidden',
          '&:focus-visible': {
            outline: `2px solid ${selectedPalette.primary.main}`,
            outlineOffset: '2px'
          }
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: tokens.shadows.glow.primary
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${selectedPalette.primary.main} 0%, ${selectedPalette.primary.dark} 100%)`,
          color: selectedPalette.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${selectedPalette.primary.light} 0%, ${selectedPalette.primary.main} 100%)`
          }
        },
        outlined: {
          borderWidth: '2px',
          borderColor: alpha(selectedPalette.primary.main, 0.5),
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alpha(selectedPalette.primary.main, 0.1),
            borderColor: selectedPalette.primary.main
          }
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.1)
          }
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.8125rem'
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.0625rem'
        }
      }
    },

    // -------------------------------------------------------------------------
    // CARDS - Solid surfaces with border-first depth
    // -------------------------------------------------------------------------
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: selectedPalette.background.paper,
          border: `1px solid ${selectedPalette.divider}`,
          borderRadius: tokens.radius.lg,
          boxShadow: 'none',
          transition: `all ${tokens.transitions.normal}`,
          '&:hover': {
            borderColor: alpha(selectedPalette.primary.main, 0.15),
            transform: 'translateY(-2px)',
            boxShadow: tokens.shadows.xs
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px'
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // PAPER - Dark surfaces
    // -------------------------------------------------------------------------
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: selectedPalette.background.paper
        },
        outlined: {
          borderColor: alpha('#ffffff', 0.08)
        }
      }
    },

    // -------------------------------------------------------------------------
    // APP BAR & NAVIGATION
    // -------------------------------------------------------------------------
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(selectedPalette.background.default, 0.8),
          backdropFilter: tokens.blur.md,
          boxShadow: 'none',
          borderBottom: `1px solid ${alpha('#ffffff', 0.06)}`
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: selectedPalette.background.paper,
          borderRight: `1px solid ${alpha('#ffffff', 0.06)}`
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          margin: '4px 8px',
          padding: '12px 16px',
          transition: `all ${tokens.transitions.fast}`,
          '&:hover': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.1)
          },
          '&.Mui-selected': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.15),
            borderLeft: `3px solid ${selectedPalette.primary.main}`,
            '&:hover': {
              backgroundColor: alpha(selectedPalette.primary.main, 0.2)
            }
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // CHIPS & BADGES - Vibrant
    // -------------------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: tokens.radius.sm,
          backgroundColor: alpha(selectedPalette.primary.main, 0.15),
          color: selectedPalette.primary.main,
          border: `1px solid ${alpha(selectedPalette.primary.main, 0.3)}`
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            background: `linear-gradient(135deg, ${selectedPalette.primary.main} 0%, ${selectedPalette.primary.dark} 100%)`,
            color: selectedPalette.primary.contrastText
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // FORM CONTROLS - Clean, dark
    // -------------------------------------------------------------------------
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(selectedPalette.background.surface, 0.5),
            borderRadius: tokens.radius.sm,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha('#ffffff', 0.1)
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(selectedPalette.primary.main, 0.5)
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(selectedPalette.primary.main, 0.2)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: selectedPalette.primary.main,
                borderWidth: '2px'
              }
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(selectedPalette.background.surface, 0.5),
          borderRadius: tokens.radius.sm
        }
      }
    },

    // -------------------------------------------------------------------------
    // TOOLTIPS & MENUS - Glassmorphism
    // -------------------------------------------------------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(selectedPalette.background.elevated, 0.95),
          backdropFilter: tokens.blur.sm,
          border: `1px solid ${alpha('#ffffff', 0.1)}`,
          fontSize: '0.8125rem',
          fontWeight: 500,
          padding: '10px 16px',
          borderRadius: tokens.radius.sm,
          boxShadow: tokens.shadows.lg
        },
        arrow: {
          color: alpha(selectedPalette.background.elevated, 0.95)
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: alpha(selectedPalette.background.elevated, 0.95),
          backdropFilter: tokens.blur.md,
          border: `1px solid ${alpha('#ffffff', 0.1)}`,
          borderRadius: tokens.radius.md,
          boxShadow: tokens.shadows.xl
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          margin: '4px 8px',
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.1)
          },
          '&.Mui-selected': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.15),
            '&:hover': {
              backgroundColor: alpha(selectedPalette.primary.main, 0.2)
            }
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // DIALOGS & MODALS - Dark glass
    // -------------------------------------------------------------------------
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: alpha(selectedPalette.background.paper, 0.95),
          backdropFilter: tokens.blur.lg,
          border: `1px solid ${alpha('#ffffff', 0.1)}`,
          borderRadius: tokens.radius.xl,
          boxShadow: tokens.shadows.xl
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamilyDisplay,
          fontWeight: 700,
          fontSize: '1.5rem',
          padding: '24px 24px 16px'
        }
      }
    },

    // -------------------------------------------------------------------------
    // TABS - Underline accent
    // -------------------------------------------------------------------------
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: `linear-gradient(90deg, ${selectedPalette.primary.main} 0%, ${selectedPalette.accent.teal} 100%)`
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          minHeight: 56,
          color: selectedPalette.text.secondary,
          '&.Mui-selected': {
            color: selectedPalette.primary.main
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // ALERTS - Vibrant borders
    // -------------------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          borderWidth: '1px',
          borderStyle: 'solid',
          backgroundColor: alpha(selectedPalette.background.surface, 0.5),
          backdropFilter: tokens.blur.sm
        },
        standardSuccess: {
          borderColor: selectedPalette.success.main,
          '& .MuiAlert-icon': { color: selectedPalette.success.main }
        },
        standardInfo: {
          borderColor: selectedPalette.info.main,
          '& .MuiAlert-icon': { color: selectedPalette.info.main }
        },
        standardWarning: {
          borderColor: selectedPalette.warning.main,
          '& .MuiAlert-icon': { color: selectedPalette.warning.main }
        },
        standardError: {
          borderColor: selectedPalette.error.main,
          '& .MuiAlert-icon': { color: selectedPalette.error.main }
        }
      }
    },

    // -------------------------------------------------------------------------
    // ICON BUTTONS - Subtle glow
    // -------------------------------------------------------------------------
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          transition: `all ${tokens.transitions.fast}`,
          '&:hover': {
            backgroundColor: alpha(selectedPalette.primary.main, 0.1),
            color: selectedPalette.primary.main
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // CHECKBOXES - Visible borders in light mode
    // -------------------------------------------------------------------------
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: mode === 'light' ? selectedPalette.text.secondary : selectedPalette.text.disabled,
          '&.Mui-checked': {
            color: selectedPalette.primary.main
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      }
    },

    // -------------------------------------------------------------------------
    // SKELETON - Dark shimmer
    // -------------------------------------------------------------------------
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#ffffff', 0.05)
        }
      }
    },

    // -------------------------------------------------------------------------
    // ACCORDION - Clean expand
    // -------------------------------------------------------------------------
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(selectedPalette.background.paper, 0.5),
          borderRadius: `${tokens.radius.md}px !important`,
          border: `1px solid ${alpha('#ffffff', 0.06)}`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '8px 0',
            borderColor: alpha(selectedPalette.primary.main, 0.2)
          }
        }
      }
    }
  }
})
}

// Default theme (dark mode)
const theme = createAppTheme('dark')

// =============================================================================
// EXPORTS
// =============================================================================

export { tokens, palette, darkPalette, lightPalette, createAppTheme }
export default theme
